import React from 'react';

export type DesktopMenuPhase = 'idle' | 'opening' | 'closing';

type LayoutStateContextValue = {
    desktopMenuOpen: boolean;
    desktopMenuPhase: DesktopMenuPhase;
    setDesktopMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setDesktopMenuPhase: React.Dispatch<React.SetStateAction<DesktopMenuPhase>>;
};

const fallbackContextValue: LayoutStateContextValue = {
    desktopMenuOpen: false,
    desktopMenuPhase: 'idle',
    setDesktopMenuOpen: () => undefined,
    setDesktopMenuPhase: () => undefined,
};

const LayoutStateContext = React.createContext<LayoutStateContextValue | null>(null);

type LayoutStateProviderProps = {
    children: React.ReactNode;
};

export const LayoutStateProvider: React.FC<LayoutStateProviderProps> = ({ children }) => {
    const [desktopMenuOpen, setDesktopMenuOpen] = React.useState<boolean>(false);
    const [desktopMenuPhase, setDesktopMenuPhase] = React.useState<DesktopMenuPhase>('idle');

    const value = React.useMemo<LayoutStateContextValue>(() => ({
        desktopMenuOpen,
        desktopMenuPhase,
        setDesktopMenuOpen,
        setDesktopMenuPhase,
    }), [desktopMenuOpen, desktopMenuPhase]);

    return (
        <LayoutStateContext.Provider value={value}>
            {children}
        </LayoutStateContext.Provider>
    );
};

export const useLayoutState = (): LayoutStateContextValue => {
    return React.useContext(LayoutStateContext) ?? fallbackContextValue;
};
